import React from 'react';
import { Collapse, Node } from '@msdyn365-commerce-modules/utilities';
import * as Msdyn365 from '@msdyn365-commerce/core';
import { ICommerceApiSettings, IGridSettings } from '@msdyn365-commerce/core-internal';
import { KeyFeaturesIcons } from '../key-features/key-features-icons';
import { AttributeValue } from '@msdyn365-commerce/retail-proxy';
import KeyFeaturesFlowering from '../key-features/key-features-flowering';
import { IProductMedia, ProductDetailMedia } from '../common/product-detail-media';
import { IDobbiesBuyboxProductDetailsConfig } from '../../dobbies-buybox-product-details.props.autogenerated';

interface IAccordionItemProps {
    key: number;
    index: number;
    title: string;
    value: string;
    itemKey: string;
    apiSettings: ICommerceApiSettings;
    keyFeaturesMeta?: AttributeValue;
    floweringTime?: string;
    isPFItem?: boolean;
    gridSettings?: IGridSettings;
    config: IDobbiesBuyboxProductDetailsConfig;
}

export function AccordionItem(props: IAccordionItemProps) {
    const { index, title, value, itemKey, keyFeaturesMeta } = props;
    const [keyFeaturesIcons, setKeyFeaturesIcons] = React.useState([]);
    const [specificationsMedia, setSpecificationsMedia] = React.useState<IProductMedia[]>([]);
    const [dimensionsMedia, setDimensionsMedia] = React.useState<IProductMedia[]>([]);
    const [productDescriptionMedia, setProductDescriptionMedia] = React.useState<IProductMedia[]>([]);
    const [instructionsAndSafetyMedia, setInstructionsAndSafetyMedia] = React.useState<IProductMedia[]>([]);
    const [deliveryAndReturnsMedia, setDeliveryAndReturnsMedia] = React.useState<IProductMedia[]>([]);
    const [isActive, setIsActive] = React.useState<boolean>(index === 0 ? true : false);

    const _toggleCollapse = () => {
        setIsActive(!isActive);
    };

    const toSlug = (s: string): string =>
        s
            .toLowerCase()
            .trim()
            .replace(/[&\s]+/g, '-')
            .replace(/[^a-z0-9-]+/g, '-')
            .replace(/-+/g, '-')
            .replace(/^-+|-+$/g, '');

    const slug = React.useMemo(() => toSlug(itemKey), [itemKey]);
    React.useEffect(() => {
        if (keyFeaturesMeta && keyFeaturesMeta.TextValue && keyFeaturesMeta.TextValue !== '') {
            try {
                const parsedIconsData = JSON.parse(keyFeaturesMeta.TextValue);
                setKeyFeaturesIcons(parsedIconsData[0].KeyFeaturesIcons);
                setDimensionsMedia(parsedIconsData[0].DimensionsMedia);
                setSpecificationsMedia(parsedIconsData[0].SpecificationsMedia);
                setProductDescriptionMedia(parsedIconsData[0].ProductdescriptionMedia);
                setInstructionsAndSafetyMedia(parsedIconsData[0]['Instructions&safetyMedia']);
                setDeliveryAndReturnsMedia(parsedIconsData[0]['Delivery&returnsMedia']);
            } catch (error) {
                console.error('Error parsing key features meta data', error);
            }
        }
    }, [value]);

    React.useEffect(() => {
        const applyHash = () => {
            const hash = window.location.hash.replace(/^#/, '');
            if (hash === slug) {
                setIsActive(true);
                document.getElementById(slug)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
        };
        applyHash();
        window.addEventListener('hashchange', applyHash);
        return () => window.removeEventListener('hashchange', applyHash);
    }, [slug]);

    React.useEffect(() => {
        if (window.location.hash) {
            history.replaceState(null, document.title, window.location.pathname + window.location.search);
        }
    }, []);

    React.useEffect(() => {
        const el = document.getElementById(slug);
        if (!el) {
            return;
        }

        const openHandler = () => setIsActive(true);

        el.addEventListener('openAccordion', openHandler);
        return () => el.removeEventListener('openAccordion', openHandler);
    }, [slug]);

    const isKeyFeaturesSection = itemKey === 'Key features';
    const isDimensionsSection = itemKey === 'Dimensions';
    const isSpecificationsSection = itemKey === 'Specifications';
    const isProductDescriptionSection = itemKey === 'Product description';
    const isInstructionsAndSafetySection = itemKey === 'Instructions & safety';
    const isDeliveryAndReturnsSection = itemKey === 'Delivery & returns';

    let attributeMedia: IProductMedia[] = [];
    switch (true) {
        case isDimensionsSection:
            attributeMedia = dimensionsMedia;
            break;
        case isSpecificationsSection:
            attributeMedia = specificationsMedia;
            break;
        case isProductDescriptionSection:
            attributeMedia = productDescriptionMedia;
            break;
        case isInstructionsAndSafetySection:
            attributeMedia = instructionsAndSafetyMedia;
            break;
        case isDeliveryAndReturnsSection:
            attributeMedia = deliveryAndReturnsMedia;
            break;
        default:
            attributeMedia = [];
    }

    const showFloweringTime = props.floweringTime && props.floweringTime !== '';

    return value && value !== '' ? (
        <Node className='ms-collapse-name' tag='li' key={props.key} id={slug}>
            <Node
                className={isActive ? 'ms-collapse-name_expanded' : 'ms-collapse-name_collapsed'}
                onClick={_toggleCollapse}
                aria-expanded={isActive}
                tag='h3'
                key={index}
            >
                <strong>{title}</strong>
            </Node>
            <Collapse className={'ms-collapse-text'} isOpen={isActive} timeout={100}>
                {isKeyFeaturesSection && ((keyFeaturesIcons && keyFeaturesIcons.length > 0) || showFloweringTime) && (
                    <Node className='key-features-section'>
                        {<KeyFeaturesIcons icons={keyFeaturesIcons} apiSettings={props.apiSettings} />}
                        {showFloweringTime && <KeyFeaturesFlowering floweringTime={props.floweringTime} config={props.config} />}
                    </Node>
                )}
                <Msdyn365.RichTextComponent text={value} />
                {attributeMedia.length > 0 && (
                    <ProductDetailMedia media={attributeMedia} apiSettings={props.apiSettings} gridSettings={props.gridSettings} />
                )}
                {/* {isKeyFeaturesSection() && (
                    <KeyFeaturesMedia media={keyFeaturesMedia} apiSettings={props.apiSettings} gridSettings={props.gridSettings} />
                )} */}
            </Collapse>
        </Node>
    ) : (
        <></>
    );
}
